import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import {
    FileText, Upload, Loader2, CheckCircle, AlertTriangle, Eye,
    ChevronRight, Shield, Star, X, Briefcase, ArrowRight, Zap,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ATSCVReview() {
    const { getAuthHeader } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [previewCV, setPreviewCV] = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [matchResults, setMatchResults] = useState(null); // {status, matches}
    const [matchPolling, setMatchPolling] = useState(false);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setResult(null);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/ats/cv/upload`, formData, {
                headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
            });
            setResult(res.data);
            if (res.data.duplicate) {
                toast.info('Duplicate CV — showing existing parsed data');
            } else {
                toast.success('CV parsed successfully');
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); }
    };

    const handleAccept = async () => {
        if (!result?.cv_id) return;
        setAccepting(true);
        setMatchResults(null);
        try {
            const res = await axios.post(`${API}/ats/cv/${result.cv_id}/accept`, {}, { headers: getAuthHeader() });
            const applicantId = res.data.applicant_id;
            toast.success(res.data.created ? 'Applicant created — matching to open jobs...' : 'Applicant updated — matching to open jobs...');
            setResult(null);

            // Poll for auto-match results
            if (res.data.matching_started) {
                setMatchPolling(true);
                setMatchResults({ status: 'pending', matches: [] });
                const pollInterval = setInterval(async () => {
                    try {
                        const matchRes = await axios.get(`${API}/ats/cv/match-results/${applicantId}`, { headers: getAuthHeader() });
                        if (matchRes.data.status !== 'pending') {
                            clearInterval(pollInterval);
                            setMatchPolling(false);
                            setMatchResults(matchRes.data);
                            if (matchRes.data.matches?.length > 0) {
                                toast.success(`Matched to ${matchRes.data.matches.length} open job${matchRes.data.matches.length !== 1 ? 's' : ''}!`);
                            } else {
                                toast.info('No strong matches found for open jobs');
                            }
                        }
                    } catch {
                        clearInterval(pollInterval);
                        setMatchPolling(false);
                    }
                }, 3000);
                // Safety timeout: stop polling after 3 minutes
                setTimeout(() => { clearInterval(pollInterval); setMatchPolling(false); }, 180000);
            }
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept CV data'); }
        finally { setAccepting(false); }
    };

    const confidence = result ? Math.round((result.confidence || 0) * 100) : 0;
    const profile = result?.parsed_profile || {};
    const flags = result?.injection_flags || [];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                    CV Review
                </h1>
                <p className="text-sm text-stone-500 mt-1">Upload and review CVs with AI-powered parsing</p>
            </div>

            {/* Upload Area */}
            <div className="bg-white rounded-xl border border-stone-200 p-8">
                <div className="max-w-lg mx-auto text-center">
                    <div className="border-2 border-dashed border-stone-200 rounded-xl p-10 hover:border-stone-300 transition-colors">
                        <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                        <p className="text-stone-700 font-medium mb-1">Drop a CV here or click to upload</p>
                        <p className="text-xs text-stone-400 mb-6">PDF, DOCX, or DOC — Max 10MB</p>
                        <label className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-xl text-sm cursor-pointer hover:bg-stone-800 transition-colors">
                            {uploading
                                ? <><Loader2 className="w-4 h-4 animate-spin" /> Parsing with AI...</>
                                : <><Upload className="w-4 h-4" /> Choose File</>}
                            <input type="file" accept=".pdf,.docx,.doc" className="hidden" onChange={handleUpload} disabled={uploading} />
                        </label>
                    </div>
                </div>
            </div>

            {/* Auto-Match Results */}
            {(matchPolling || matchResults) && (
                <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-stone-100 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center">
                            <Zap className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                                Auto-Match Results
                            </h3>
                            <p className="text-[11px] text-stone-400">
                                {matchPolling ? 'Matching against open job orders...' : `${matchResults?.matches?.length || 0} job${(matchResults?.matches?.length || 0) !== 1 ? 's' : ''} matched`}
                            </p>
                        </div>
                        {matchPolling && <Loader2 className="w-4 h-4 animate-spin text-blue-500 ml-auto" />}
                    </div>

                    {matchPolling && (
                        <div className="px-6 py-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin text-stone-300 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">Scoring applicant against all open job orders...</p>
                            <p className="text-xs text-stone-400 mt-1">This may take a moment</p>
                        </div>
                    )}

                    {!matchPolling && matchResults?.matches?.length > 0 && (
                        <div className="divide-y divide-stone-100">
                            {matchResults.matches.map((m, i) => (
                                <div key={i} className="px-6 py-3 flex items-center gap-4 hover:bg-stone-50/50">
                                    <div className={`text-lg font-bold min-w-[48px] text-center ${
                                        m.score >= 80 ? 'text-emerald-600' :
                                        m.score >= 60 ? 'text-blue-600' :
                                        m.score >= 40 ? 'text-amber-600' :
                                        'text-stone-400'
                                    }`}>
                                        {m.score}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-stone-800 truncate">{m.job_title}</p>
                                        <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{m.reasoning}</p>
                                    </div>
                                    <span className={`text-[10px] font-medium px-2 py-1 rounded-full whitespace-nowrap ${
                                        m.recommendation === 'strong_match' ? 'bg-emerald-100 text-emerald-700' :
                                        m.recommendation === 'good_match' ? 'bg-blue-100 text-blue-700' :
                                        m.recommendation === 'partial_match' ? 'bg-amber-100 text-amber-700' :
                                        'bg-stone-100 text-stone-600'
                                    }`}>
                                        {(m.recommendation || '').replace(/_/g, ' ')}
                                    </span>
                                    <a href={`/recruitment/jobs/${m.job_id}`}
                                        className="text-stone-400 hover:text-stone-600">
                                        <ArrowRight className="w-4 h-4" />
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                    {!matchPolling && matchResults?.matches?.length === 0 && (
                        <div className="px-6 py-8 text-center">
                            <Briefcase className="w-8 h-8 text-stone-200 mx-auto mb-2" />
                            <p className="text-sm text-stone-500">No strong matches found for current open jobs</p>
                            <p className="text-xs text-stone-400 mt-1">The applicant has been added to the talent pool and will be matched to future jobs</p>
                        </div>
                    )}

                    {!matchPolling && matchResults?.error && (
                        <div className="px-6 py-4 bg-red-50 text-xs text-red-600">
                            Matching error: {matchResults.error}
                        </div>
                    )}

                    {!matchPolling && matchResults && (
                        <div className="px-6 py-3 border-t border-stone-100 bg-stone-50/50 flex justify-end">
                            <Button variant="outline" size="sm" className="text-xs" onClick={() => { setMatchResults(null); }}>
                                Dismiss
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Parse Result */}
            {result && (
                <div className="space-y-4">
                    {/* Confidence + Flags Header */}
                    <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${
                            confidence >= 70 ? 'bg-emerald-50 text-emerald-700' :
                            confidence >= 40 ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                        }`}>
                            <Star className="w-4 h-4" />
                            {confidence}% confidence
                        </div>
                        <span className="text-xs text-stone-400">Engine: {result.engine || 'gpt4o'}</span>
                        {result.duplicate && (
                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">Duplicate detected</span>
                        )}
                        {flags.length > 0 && (
                            <span className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-2 py-1 rounded">
                                <Shield className="w-3.5 h-3.5" /> {flags.length} injection flag{flags.length !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>

                    {/* Injection Flags */}
                    {flags.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2">
                            <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
                                <AlertTriangle className="w-4 h-4" /> Potential Prompt Injection Detected
                            </div>
                            {flags.map((f, i) => (
                                <div key={i} className="text-xs text-red-700 bg-red-100/50 rounded p-2">
                                    <span className="font-medium">{f.type}</span>
                                    <span className={`ml-2 px-1 py-0.5 rounded text-[10px] ${
                                        f.severity === 'high' ? 'bg-red-200' : 'bg-amber-200'
                                    }`}>{f.severity}</span>
                                    {f.content && <p className="mt-1 font-mono text-[10px] opacity-80">...{f.content}...</p>}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Parsed Profile Card */}
                    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone-100">
                            {/* Left: Structured Data */}
                            <div className="p-6 space-y-4">
                                <h3 className="font-semibold text-stone-900">Extracted Profile</h3>
                                <div className="space-y-3 text-sm">
                                    <Field label="Name" value={profile.name} />
                                    <Field label="Email" value={profile.email} />
                                    <Field label="Phone" value={profile.phone} />
                                    <Field label="Headline" value={profile.headline} />
                                    <Field label="Location" value={
                                        profile.location ? `${profile.location.city || ''}${profile.location.country ? ', ' + profile.location.country : ''}` : null
                                    } />
                                    <Field label="Availability" value={profile.availability?.replace(/_/g, ' ')} />

                                    {(profile.skills || []).length > 0 && (
                                        <div>
                                            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Skills ({profile.skills.length})</p>
                                            <div className="flex flex-wrap gap-1">
                                                {profile.skills.map((s, i) => (
                                                    <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                                        {s.name || s}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(profile.work_history || []).length > 0 && (
                                        <div>
                                            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Experience ({profile.work_history.length} roles)</p>
                                            {profile.work_history.slice(0, 3).map((w, i) => (
                                                <div key={i} className="py-1.5 border-b border-stone-100 last:border-0">
                                                    <p className="font-medium text-stone-800">{w.title}</p>
                                                    <p className="text-xs text-stone-500">{w.company} ({w.start_date} — {w.end_date || 'Present'})</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {(profile.education || []).length > 0 && (
                                        <div>
                                            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Education</p>
                                            {profile.education.map((e, i) => (
                                                <p key={i} className="text-stone-600">{e.degree} {e.field} — {e.institution} ({e.year})</p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Right: Markdown Preview */}
                            <div className="p-6">
                                <h3 className="font-semibold text-stone-900 mb-3">AI-Generated Profile</h3>
                                <div className="prose prose-stone prose-sm max-w-none max-h-[500px] overflow-y-auto bg-stone-50 rounded-lg p-4 text-xs"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((result.parsed_markdown || '').replace(/\n/g, '<br/>')) }} />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="border-t border-stone-100 px-6 py-4 flex items-center justify-between bg-stone-50/50">
                            <Button variant="outline" size="sm" onClick={() => setResult(null)}>
                                <X className="w-4 h-4 mr-1" /> Discard
                            </Button>
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" disabled>
                                    <Eye className="w-4 h-4 mr-1" /> Edit Before Accepting
                                </Button>
                                <Button onClick={handleAccept} disabled={accepting} size="sm"
                                    className="bg-stone-900 text-white hover:bg-stone-800">
                                    {accepting
                                        ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Processing...</>
                                        : <><CheckCircle className="w-4 h-4 mr-1" /> Accept & Create Applicant</>}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <p className="text-xs text-stone-400 uppercase tracking-wider">{label}</p>
            <p className="text-stone-700">{value}</p>
        </div>
    );
}
